import { CustomPageType, HeaderCustomization, IOperationResultNoValue } from '@ViewModels';
import { yupResolver } from '@hookform/resolvers/yup';
import { css, StyleSheet } from 'aphrodite';
import equal from 'fast-deep-equal';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { useModal } from '../../../../../models/hooks/useModal';
import { useRemoveAccountLogoMutation, useUploadAccountLogoMutation } from '../../../../../queries';
import { invalidateAccountAdminQuery } from '../../../../../queries/Admin/Account/useAdminAccountQuery';
import { useDeleteBlogStyleMutation } from '../../../../../queries/CustomPageStyle/useDeleteBlogStyleMutation';
import { useFindOrCreateAndUpdateBlogStyleMutation } from '../../../../../queries/CustomPageStyle/useFindOrCreateAndUpdateBlogStyleMutation';
import { invalidateAllHostedBlogPagePreviews } from '../../../../../queries/HostedBlog/useHostedBlogPagePreviewQuery';
import { invalidateSchedulerPreviewCustomPageStyleQueries } from '../../../../../queries/Scheduler/useSchedulerPreviewCustomPageStyleQuery';
import { invalidateSurveyPreviewQuery } from '../../../../../queries/Surveys/useSurveyPreviewQuery';
import { Button } from '../../../../../web/components-v2/Button';
import { Field, FieldError, FieldLabel } from '../../../../../web/components-v2/Field';
import { Input } from '../../../../../web/components-v2/Input';
import { THEME } from '../../../../../web/components-v2/Theme/theme';
import {
	ConfirmationDialog,
	DefaultConfirmationOptions,
	DefaultDeleteConfirmationOptions,
} from '../../../../../web/components/ConfirmationDialog';
import { ImageFileChooserModal } from '../../../../../web/components/ImageFileChooserModal';
import { LoadingSpinner } from '../../../../../web/components/LoadingSpinner';
import { WarningIcon } from '../../../../../web/components/svgs/icons/WarningIcon';
import { Toggle } from '../../../../../web/components/Toggle';
import { baseStyleSheet, bs } from '../../../../../web/styles/styles';
import { useAccountContext } from '../../../../components/blogs/AccountContext';
import { FontSelect } from '../../../../components/FontSelect/FontSelect';
import { InfoPopover } from '../../../../components/InfoPopover/InfoPopover';
import { Row } from '../../../../components/Layout';
import { ColorField } from '../components/ColorField';
import { NavLinkBuilder } from '../components/NavLinkBuilder';
import { SettingCard } from '../components/SettingCard';
import { applyPresetToHeaderForm } from '../applyPreset';
import { PreviewContext } from '../PreviewContextTabs';
import { StylePreset } from '../presets';
import {
	DEFAULT_HEADER_FORM_VALUES,
	DEFAULT_PRIMARY_BUTTON_DISPLAY_COLORS,
	MAX_FONT_SIZE_PX,
	MIN_FONT_SIZE_PX,
} from './constants';
import { FeatureToggleSection } from './FeatureToggleSection';
import { getFontSizePlaceholder, parseOptionalFontSize } from './fontSizeHelpers';
import { ResponsiveFontSizeInputs } from './ResponsiveFontSizeInputs';
import { headerSchema } from './schemas';
import { TemplateFormWrapper } from './TemplateFormWrapper';

interface HeaderDesignFormProps {
	customContent?: HeaderCustomization;
	previewContext?: PreviewContext;
	onUpdateSuccess: () => void;
	onFormDirty: () => void;
	onSavingChange?: (saving: boolean) => void;
}

export interface HeaderDesignFormRef {
	applyPreset: (preset: StylePreset) => void;
	submit?: () => void;
}

export const HeaderDesignForm = forwardRef<HeaderDesignFormRef, HeaderDesignFormProps>(
	({ customContent, previewContext = 'blog', onFormDirty, onUpdateSuccess, onSavingChange }, ref) => {
		const errorMessages = useErrorMessages();
		const { account, accountId, impersonationContext } = useAccountContext();
		const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
		const [showLogoFileInputModal, setShowLogoFileInputModal] = useState(false);

		// Logo mutations
		const uploadLogoMutation = useUploadAccountLogoMutation({
			onSuccess: () => {
				invalidateAccountAdminQuery();
				invalidateAllHostedBlogPagePreviews();
				invalidateSurveyPreviewQuery();
				invalidateSchedulerPreviewCustomPageStyleQueries();
				setShowLogoFileInputModal(false);
			},
			onError: error => {
				setShowLogoFileInputModal(false);
				errorMessages.pushApiError(error);
			},
		});

		const removeLogoMutation = useRemoveAccountLogoMutation({
			onSuccess: () => {
				invalidateAccountAdminQuery();
				invalidateAllHostedBlogPagePreviews();
				invalidateSurveyPreviewQuery();
				invalidateSchedulerPreviewCustomPageStyleQueries();
			},
			onError: error => {
				errorMessages.pushApiError(error);
			},
		});

		const deleteLogoConfirmationModal = useModal(false, () => {
			removeLogoMutation.mutate({ impersonationContext });
		}, [removeLogoMutation, impersonationContext]);

		const logoUrl = account?.preferences?.logo?.url;
		const isLogoLoading = uploadLogoMutation.isPending || removeLogoMutation.isPending;

		// Determine which features are available for this account
		const blogFeature = account?.features?.blogFeature;
		const blogFeatureAvailable = !!blogFeature?.enabled && !blogFeature?.dudaSiteId;
		const surveyFeatureAvailable = !!account?.features?.surveys?.enabled;
		const meetingFeatureAvailable = !!account?.features?.meetingScheduler?.enabled;
		const filesFeatureAvailable = !!account?.features?.files?.enabled && surveyFeatureAvailable;

		const showMainPageTitle = previewContext === 'blog';
		const showPageBodyCard = previewContext !== 'blog';

		const form = useForm<HeaderCustomization>({
			defaultValues: DEFAULT_HEADER_FORM_VALUES,
			mode: 'onBlur',
			resolver: yupResolver(headerSchema),
		});
		const {
			formState: { errors, isDirty },
			handleSubmit,
			register,
			setValue,
			getValues,
			watch,
		} = form;
		const overallFont = watch('overallFont', {
			fontName: '',
			fontUri: '',
		});
		const dropdownFont = watch('dropdown.font', {
			fontName: '',
			fontUri: '',
		});
		const dropdownFontSize = watch('dropdown.fontSizePx');
		const bodyFont = watch('bodyFont', {
			fontName: '',
			fontUri: '',
		});
		const primaryButtonBackground = watch('primaryButton.backgroundColor');
		const primaryButtonTextColor = watch('primaryButton.color');
		const primaryButtonHoverColor = watch('primaryButton.hoverColor');

		// Watch color values for live updating
		const textColor = watch('textColor');
		const backgroundColor = watch('backgroundColor');
		const hoverColor = watch('hoverColor');
		const dropdownTextColor = watch('dropdown.color');
		const dropdownBackgroundColor = watch('dropdown.backgroundColor');

		// Watch enabledFor for feature toggles
		const enabledFor = watch('enabledFor');

		// Watch logoOnlyHeader so the menu items table can be hidden when it's on.
		const logoOnlyHeader = watch('logoOnlyHeader');
		const watchedMenuLinks = watch('menuLinks');
		const hasNonEmptyMenuLinks = !!watchedMenuLinks?.some(link => !!link?.label || !!link?.destination);

		const handleEnabledForChange = useCallback(
			(newEnabledFor: CustomPageType[]) => {
				setValue('enabledFor', newEnabledFor, { shouldDirty: true });
			},
			[setValue]
		);

		const handleLogoOnlyHeaderChange = useCallback(
			(checked: boolean) => {
				setValue('logoOnlyHeader', checked, { shouldDirty: true });
				if (checked) {
					// Hide and clear any in-memory menu link rows; user can re-add links if they turn the toggle off.
					setValue('menuLinks', [], { shouldDirty: true });
				}
			},
			[setValue]
		);

		const updateHeaderMutation = useFindOrCreateAndUpdateBlogStyleMutation({
			onSuccess: () => {
				form.reset(getValues());
				onUpdateSuccess();
				toast.success('Saved changes successfully');
			},
			onError: (error: IOperationResultNoValue) => {
				errorMessages.pushApiError(error);
			},
		});

		const deleteHeaderMutation = useDeleteBlogStyleMutation({
			onSuccess: () => {
				form.reset(DEFAULT_HEADER_FORM_VALUES);
				onUpdateSuccess();
				toast.success('Customization deleted successfully');
				setShowDeleteDialog(false);
			},
			onError: (error: IOperationResultNoValue) => {
				errorMessages.pushApiError(error);
				setShowDeleteDialog(false);
			},
		});

		const onValid: SubmitHandler<HeaderCustomization> = (data, event) => {
			event?.preventDefault?.();
			event?.stopPropagation?.();

			updateHeaderMutation.mutate({
				id: accountId,
				impersonationContext,
				style: data,
			});
		};

		const handleDeleteCustomization = () => {
			setShowDeleteDialog(true);
		};

		const handleDeleteConfirm = (_: never, canceled: boolean) => {
			if (!canceled && customContent && customContent.id) {
				deleteHeaderMutation.mutate({
					id: customContent.id,
					impersonationContext,
				});
			} else {
				setShowDeleteDialog(false);
			}
		};

		useEffect(() => {
			if (customContent && !equal(customContent, getValues())) {
				Object.entries(customContent).forEach(entry => {
					if (entry[1] !== undefined && entry[1] !== null) {
						setValue(entry[0] as Extract<keyof HeaderCustomization, string>, entry[1]);
					}
				});
			}
		}, [customContent, getValues, setValue]);

		useEffect(() => {
			if (isDirty) {
				onFormDirty();
			}
		}, [isDirty, onFormDirty]);

		useEffect(() => {
			onSavingChange?.(updateHeaderMutation.isPending);
		}, [updateHeaderMutation.isPending, onSavingChange]);

		useImperativeHandle(ref, () => ({
			applyPreset: (preset: StylePreset) => {
				const currentValues = getValues();
				const updatedValues = applyPresetToHeaderForm(preset, currentValues);

				(Object.keys(updatedValues) as (keyof HeaderCustomization)[]).forEach(key => {
					const value = updatedValues[key];
					if (value !== undefined) {
						setValue(key, value, { shouldDirty: true });
					}
				});
			},
			submit: () => handleSubmit(onValid)(),
		}));

		return (
			<>
				<FormProvider {...form}>
					<TemplateFormWrapper
						chromeless
						title='Customize Header'
						infoMessage='This form is applicable for both blogs and surveys.'
						isLoading={updateHeaderMutation.isPending}
						errors={errors}
						onSubmit={handleSubmit(onValid)}
						onDeleteCustomization={handleDeleteCustomization}
						isDeleting={deleteHeaderMutation.isPending}
						hasCustomization={!!customContent && !!customContent.id}
					>
						<SettingCard
							title='Logo & banner'
							infoMessage='This logo is account-wide and will appear across all blogs, surveys, and meetings for this account.'
						>
							<div className={css(s.logoRow)}>
								{logoUrl ? (
									<>
										<img src={logoUrl} alt='Account logo' className={css(s.accountLogo)} />
										{isLogoLoading ? (
											<LoadingSpinner type='extra-tiny' className={css(baseStyleSheet.inlineBlock)} />
										) : (
											<>
												<Button variant='link' type='button' onClick={() => setShowLogoFileInputModal(true)}>
													Edit
												</Button>
												<Button
													variant='link'
													type='button'
													onClick={() => deleteLogoConfirmationModal.setIsOpen(true)()}
												>
													Remove
												</Button>
											</>
										)}
									</>
								) : (
									<Button variant='outline' type='button' onClick={() => setShowLogoFileInputModal(true)}>
										Upload logo
									</Button>
								)}
							</div>
							{showMainPageTitle && (
								<Field>
									<FieldLabel htmlFor='header-main-page-title'>Main page title</FieldLabel>
									<Input id='header-main-page-title' placeholder='Blogs' {...register('mainPageTitle')} />
									<FieldError errors={errors?.mainPageTitle ? [errors.mainPageTitle] : []} />
								</Field>
							)}
							<FontSelect
								initialValue={customContent?.overallFont}
								onChange={val => setValue('overallFont', val, { shouldDirty: true })}
								value={overallFont}
								labelAccessory={
									<InfoPopover message='This font will be used as a global default for your blog. You will be able to make further selections for individual components.' />
								}
							/>
							<div className={css(s.colorGrid2)}>
								<ColorField
									id='header-text-color'
									label='Text'
									value={textColor}
									fallback='#000000'
									onChange={color => setValue('textColor', color, { shouldDirty: true })}
								/>
								<ColorField
									id='header-background-color'
									label='Background'
									value={backgroundColor}
									fallback='#ffffff'
									onChange={color => setValue('backgroundColor', color, { shouldDirty: true })}
								/>
								<ColorField
									id='header-hover-color'
									label='Hover'
									value={hoverColor}
									fallback='#f0f0f0'
									onChange={color => setValue('hoverColor', color, { shouldDirty: true })}
								/>
							</div>
						</SettingCard>

						<SettingCard title='Navigation menu'>
							{!logoOnlyHeader && <NavLinkBuilder name='menuLinks' nested />}
							<div className={css(s.divider)} />
							<span className={css(s.sectionSubLabel)}>Dropdown</span>
							<div className={css(s.colorGrid2)}>
								<ColorField
									id='header-dropdown-text-color'
									label='Text'
									value={dropdownTextColor}
									fallback='#000000'
									onChange={color => setValue('dropdown.color', color, { shouldDirty: true })}
								/>
								<ColorField
									id='header-dropdown-background-color'
									label='Background'
									value={dropdownBackgroundColor}
									fallback='#ffffff'
									onChange={color => setValue('dropdown.backgroundColor', color, { shouldDirty: true })}
								/>
							</div>
							<ResponsiveFontSizeInputs
								desktopError={errors?.dropdown?.fontSizePx}
								desktopInputProps={{
									placeholder: getFontSizePlaceholder(dropdownFontSize),
									...register('dropdown.fontSizePx', { setValueAs: parseOptionalFontSize }),
								}}
								desktopLabel='Dropdown Size'
								max={MAX_FONT_SIZE_PX}
								min={MIN_FONT_SIZE_PX}
								mobileError={errors?.dropdown?.mobileFontSizePx}
								mobileInputProps={{
									placeholder: getFontSizePlaceholder(undefined, dropdownFontSize),
									...register('dropdown.mobileFontSizePx', { setValueAs: parseOptionalFontSize }),
								}}
								mobileLabel='Mobile Dropdown Size'
								step={1}
							/>
							<FontSelect
								initialValue={customContent?.dropdown?.font}
								onChange={val => setValue('dropdown.font', val, { shouldDirty: true })}
								value={dropdownFont}
							/>
						</SettingCard>

						{showPageBodyCard && (
							<SettingCard
								title='Page body'
								infoMessage='These settings style the body of your public survey, files, event, and meeting pages. Leave them unset to keep the Levitate defaults.'
							>
								<FontSelect
									initialValue={customContent?.bodyFont}
									onChange={val => setValue('bodyFont', val, { shouldDirty: true })}
									value={bodyFont}
								/>
								<div className={css(s.colorGrid3)}>
									<ColorField
										id='header-primary-button-background'
										label='Button'
										value={primaryButtonBackground || DEFAULT_PRIMARY_BUTTON_DISPLAY_COLORS.backgroundColor}
										onChange={color => setValue('primaryButton.backgroundColor', color, { shouldDirty: true })}
									/>
									<ColorField
										id='header-primary-button-text'
										label='Button text'
										value={primaryButtonTextColor || DEFAULT_PRIMARY_BUTTON_DISPLAY_COLORS.color}
										onChange={color => setValue('primaryButton.color', color, { shouldDirty: true })}
									/>
									<ColorField
										id='header-primary-button-hover'
										label='Button hover'
										value={primaryButtonHoverColor || DEFAULT_PRIMARY_BUTTON_DISPLAY_COLORS.hoverColor}
										onChange={color => setValue('primaryButton.hoverColor', color, { shouldDirty: true })}
									/>
								</div>
							</SettingCard>
						)}

						<SettingCard
							title='Apply to features'
							infoMessage='Choose which features should use this custom styling. When disabled, the feature will use default system styling.'
						>
							<FeatureToggleSection
								hideHeading
								enabledFor={enabledFor}
								onEnabledForChange={handleEnabledForChange}
								blogFeatureAvailable={blogFeatureAvailable}
								surveyFeatureAvailable={surveyFeatureAvailable}
								meetingFeatureAvailable={meetingFeatureAvailable}
								filesFeatureAvailable={filesFeatureAvailable}
								disabled={updateHeaderMutation.isPending}
							/>
							<div className={css(s.divider)} />
							<Row className={css(bs.itemsCenter, bs.gap2)}>
								<Toggle
									id='logo-only-header-toggle'
									isOn={!!logoOnlyHeader}
									onToggleCheckChanged={handleLogoOnlyHeaderChange}
									disabled={hasNonEmptyMenuLinks}
									text='Logo-only header'
								/>
								<InfoPopover message='Turn this on if you want your logo to appear on surveys and meeting pages without configuring menu links or custom colors.' />
							</Row>
							{hasNonEmptyMenuLinks && (
								<div className={css(bs.textSm, bs.textGray600)}>Remove all menu links to enable logo-only header.</div>
							)}
						</SettingCard>
					</TemplateFormWrapper>
				</FormProvider>
				{showDeleteDialog && (
					<ConfirmationDialog
						icon={<WarningIcon />}
						modalProps={{ isOpen: showDeleteDialog, onRequestClose: handleDeleteConfirm }}
						options={DefaultConfirmationOptions}
						title='Are you sure you want to delete this customization? This action cannot be undone.'
					/>
				)}
				<ImageFileChooserModal
					title='Upload Logo'
					modalProps={{
						isOpen: showLogoFileInputModal,
						onRequestClose: (result, cancel) => {
							if (!cancel && result) {
								uploadLogoMutation.mutate({ logo: result, impersonationContext });
							} else {
								setShowLogoFileInputModal(false);
							}
						},
					}}
				/>
				<ConfirmationDialog
					icon={<WarningIcon />}
					modalProps={deleteLogoConfirmationModal}
					options={DefaultDeleteConfirmationOptions}
					title='Are you sure you want to remove the account logo?'
				/>
			</>
		);
	}
);

HeaderDesignForm.displayName = 'HeaderDesignForm';

const s = StyleSheet.create({
	logoRow: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		flexWrap: 'wrap',
	},
	accountLogo: {
		maxHeight: 70,
		maxWidth: '100%',
		objectFit: 'contain',
	},
	colorGrid2: {
		display: 'grid',
		gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
		gap: THEME.spacing.md,
		[THEME.queries.sm]: {
			gridTemplateColumns: '1fr',
		},
	},
	colorGrid3: {
		display: 'grid',
		gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
		gap: THEME.spacing.md,
		[THEME.queries.sm]: {
			gridTemplateColumns: '1fr',
		},
	},
	divider: {
		height: 1,
		backgroundColor: THEME.colors.border.light,
		border: 'none',
	},
	sectionSubLabel: {
		fontFamily: THEME.typography.fontFamilies.sora,
		fontWeight: THEME.typography.fontWeights.bold,
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},
});
